'use client';

import React, { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useLanguage } from '@/contexts/LanguageContext';
import { interpolate } from '@/lib/i18n/utils';

export function useTranslation() {
  const { language, translations, isLoading } = useLanguage();
  const nt = useTranslations();

  const t = useCallback((key: string, values?: Record<string, React.ReactNode>): string => {
    let translatedValue: unknown = translations;
    if (translations) {
      for (const segment of key.split('.')) {
        translatedValue = (translatedValue as Record<string, unknown>)?.[segment];
      }
      // Dynamic content such as CMS/Academy tags intentionally falls back to
      // its raw label. Avoid asking next-intl to throw and log for each missing
      // dynamic key during SSR or static generation.
      if (typeof translatedValue !== 'string') return key;
    }

    // next-intl's t() renders ICU placeholders ({var}) but cannot render React nodes
    // (elements / arrays / functions). Route any call carrying such a value through the
    // self-built node-aware interpolate so behavior is preserved byte-for-byte.
    const hasNodeValue = !!values && Object.values(values).some(
      (value) => value != null && (typeof value === 'object' || typeof value === 'function')
    );

    if (hasNodeValue) {
      if (!translations) {
        return '';
      }
      try {
        const result = interpolate(translatedValue as string, values);
        // interpolate may return a string or a React node array
        return typeof result === 'string' ? result : (Array.isArray(result) ? result.join('') : String(result));
      } catch (error) {
        console.error(`Translation error for key "${key}":`, error);
        return key;
      }
    }

    // String / number / boolean values: delegate to next-intl (ICU MessageFormat).
    try {
      return nt(key, values as Record<string, string | number | Date>);
    } catch (error) {
      console.error(`[next-intl] Translation error for key "${key}":`, error);
      return key;
    }
  }, [translations, nt]);

  return { t, language, isLoading };
}
